import { Add, Edit } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Chip as MuiChip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField
} from '@mui/material'
import { AddVINDialog, GreenText, OrderableTableCell } from 'components'
import { ListBody } from 'components/list'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { getToDoList as getToDoListAPI } from 'controllers/toDoLists/conformity/SendBackToCanadaController'
import { VehicleDto } from 'dtos'
import { SendBackToCanadaDto, SendBackToCanadaQueryParams } from 'dtos/toDoLists/conformity'
import { useQuery } from 'hooks'
import { DEBOUNCE_DELAY } from 'localConstants'
import moment from 'moment'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDateMoment, useDebounce } from 'utils'

export default function VehiclesEligibleForConformityToDoList() {
  const layout = useLayout()
  const navigate = useNavigate()
  const query = useQuery()

  const importBranchGuid = query.get('importBranchGuid') ?? undefined

  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [filterField, setFilterField] = useState<string>('')
  const [filterValue, setFilterValue] = useState<string>('')
  const [filterValueFrom, setFilterValueFrom] = useState<string>('')
  const [filterValueTo, setFilterValueTo] = useState<string>('')
  const [isAddingVIN, setIsAddingVIN] = useState<boolean>(false)
  const [isGettingToDoList, setIsGettingToDoList] = useState<boolean>(false)
  const [queryParams, setQueryParams] = useState<SendBackToCanadaQueryParams>({ page: 0, pageSize: 10, order: 'asc', orderBy: 'createdDateTime' })
  const [toDoList, setToDoList] = useState<SendBackToCanadaDto[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)

  const getToDoList = useDebounce((queryParams: SendBackToCanadaQueryParams) => {
    setIsGettingToDoList(true)
    getToDoListAPI(queryParams)
      .then(res => {
        setTotalCount(res.totalCount ?? 0)
        setToDoList(res.value)
      })
      .finally(() => {
        setIsGettingToDoList(false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    if (getToDoList) getToDoList(queryParams)
  }, [getToDoList, queryParams])

  useEffect(() => {
    setQueryParams({ ...queryParams, importBranchGuid })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importBranchGuid])

  useEffect(() => {
    layout.setIsLayoutLoading(isGettingToDoList)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGettingToDoList])

  useEffect(() => {
    layout.setTitle('Send Back To Canada')
    return () => layout.setTitle('')
  }, [layout])

  const onChangeOrder = (orderBy: string) => {
    setQueryParams(prev => ({
      ...queryParams,
      orderBy,
      order: prev.orderBy === orderBy && prev.order === 'asc' ? 'desc' : 'asc'
    }))
  }

  return (
    <>
      <AddVINDialog
        initialValues={new VehicleDto()}
        isLoading={isAddingVIN}
        onClose={() => setDialogOpen(false)}
        onSave={values => {
          setIsAddingVIN(true)
          controllers
            .createVehicle(values)
            .then(_ => {
              enqueueSnackbar('Vehicle Saved Successfully!', { variant: 'success' })
              setDialogOpen(false)
              getToDoList(queryParams)
            })
            .finally(() => {
              setIsAddingVIN(false)
            })
        }}
        open={dialogOpen}
      />

      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm={true} container spacing={1} alignItems='center'>
                <Grid item xs={12} sm='auto'>
                  <TextField
                    fullWidth
                    label='Filter Field'
                    onChange={e => {
                      setFilterField(e.target.value)
                      setFilterValue('')
                    }}
                    select
                    sx={{ minWidth: 200 }}
                    value={filterField || ''}
                  >
                    <MenuItem value='createdDateTime'>Created</MenuItem>

                    <MenuItem value='vehicleCheckInDateTime'>Vehicle Check-In Completed Date</MenuItem>

                    <MenuItem value='dotRejectionDateTime'>Rejection Date</MenuItem>

                    <MenuItem value='vin'>VIN</MenuItem>

                    <MenuItem value='year'>Year</MenuItem>

                    <MenuItem value='make'>Make</MenuItem>

                    <MenuItem value='model'>Model</MenuItem>
                  </TextField>
                </Grid>

                {['', 'vin', 'year', 'make', 'model'].includes(filterField) && (
                  <Grid item xs={12} sm='auto'>
                    <TextField
                      disabled={!filterField}
                      label='Filter Value'
                      onChange={e => setFilterValue(e.target.value)}
                      type={filterField === 'year' ? 'number' : 'text'}
                      value={filterValue || ''}
                    />
                  </Grid>
                )}

                {['createdDateTime', 'vehicleCheckInDateTime', 'dotRejectionDateTime'].includes(filterField) && (
                  <>
                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { max: filterValueTo ?? undefined } }}
                        label='Filter Date From'
                        onChange={e => setFilterValueFrom(e.target.value)}
                        type='date'
                        value={filterValueFrom}
                      />
                    </Grid>

                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { min: filterValueFrom ?? undefined } }}
                        label='Filter Date To'
                        onChange={e => setFilterValueTo(e.target.value)}
                        type='date'
                        value={filterValueTo}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='inherit'
                    disabled={
                      !filterField ||
                      (['createdDateTime', 'vehicleCheckInDateTime', 'dotRejectionDateTime'].includes(filterField)
                        ? !filterValueFrom || !filterValueTo
                        : !filterValue)
                    }
                    variant='contained'
                    onClick={() => {
                      if (filterField === 'createdDateTime') {
                        setQueryParams({
                          ...queryParams,
                          createdDateTimeFrom: filterValueFrom,
                          createdDateTimeTo: filterValueTo
                        })
                      } else if (filterField === 'vehicleCheckInDateTime') {
                        setQueryParams({
                          ...queryParams,
                          vehicleCheckInDateTimeFrom: filterValueFrom,
                          vehicleCheckInDateTimeTo: filterValueTo
                        })
                      } else if (filterField === 'dotRejectionDateTime') {
                        setQueryParams({
                          ...queryParams,
                          dotRejectionDateTimeFrom: filterValueFrom,
                          dotRejectionDateTimeTo: filterValueTo
                        })
                      } else {
                        setQueryParams({
                          ...queryParams,
                          [filterField]: filterValue
                        })
                      }

                      setFilterField('')
                      setFilterValue('')
                      setFilterValueFrom('')
                      setFilterValueTo('')
                    }}
                  >
                    APPLY FILTER
                  </Button>
                </Grid>

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='secondary'
                    disabled={
                      !Boolean(
                        queryParams.createdDateTimeFrom ||
                          queryParams.createdDateTimeTo ||
                          queryParams.vehicleCheckInDateTimeFrom ||
                          queryParams.vehicleCheckInDateTimeTo ||
                          queryParams.dotRejectionDateTimeFrom ||
                          queryParams.dotRejectionDateTimeTo ||
                          queryParams.vin ||
                          queryParams.year ||
                          queryParams.make ||
                          queryParams.model
                      )
                    }
                    variant='text'
                    onClick={() => {
                      setQueryParams({
                        ...queryParams,
                        createdDateTimeFrom: undefined,
                        createdDateTimeTo: undefined,
                        vehicleCheckInDateTimeFrom: undefined,
                        vehicleCheckInDateTimeTo: undefined,
                        dotRejectionDateTimeFrom: undefined,
                        dotRejectionDateTimeTo: undefined,
                        vin: undefined,
                        year: undefined,
                        make: undefined,
                        model: undefined
                      })
                    }}
                  >
                    RESET FILTERS
                  </Button>
                </Grid>
              </Grid>

              <Grid item>
                <Button
                  color='primary'
                  endIcon={<Add />}
                  onClick={() => {
                    setDialogOpen(true)
                  }}
                  variant='text'
                >
                  ADD VIN
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12} container spacing={1} alignItems='center'>
              {queryParams.createdDateTimeFrom && queryParams.createdDateTimeTo && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Created from "${moment(queryParams.createdDateTimeFrom).format('M/D/yyyy')}" to "${moment(queryParams.createdDateTimeFrom).format(
                      'M/D/yyyy'
                    )}"`}
                    onDelete={() => setQueryParams({ ...queryParams, createdDateTimeFrom: undefined, createdDateTimeTo: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.vehicleCheckInDateTimeFrom && queryParams.vehicleCheckInDateTimeTo && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Vehicle Check-In Completed Date from "${moment(queryParams.vehicleCheckInDateTimeFrom).format('M/D/yyyy')}" to "${moment(
                      queryParams.vehicleCheckInDateTimeTo
                    ).format('M/D/yyyy')}"`}
                    onDelete={() => setQueryParams({ ...queryParams, vehicleCheckInDateTimeFrom: undefined, vehicleCheckInDateTimeTo: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.dotRejectionDateTimeFrom && queryParams.dotRejectionDateTimeTo && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Rejection Date from "${moment(queryParams.dotRejectionDateTimeFrom).format('M/D/yyyy')}" to "${moment(
                      queryParams.dotRejectionDateTimeTo
                    ).format('M/D/yyyy')}"`}
                    onDelete={() => setQueryParams({ ...queryParams, dotRejectionDateTimeFrom: undefined, dotRejectionDateTimeTo: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.vin && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`VIN contains "${queryParams.vin}"`}
                    onDelete={() => setQueryParams({ ...queryParams, vin: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.year && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Year is ${queryParams.year}`}
                    onDelete={() => setQueryParams({ ...queryParams, year: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.make && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Make contains "${queryParams.make}"`}
                    onDelete={() => setQueryParams({ ...queryParams, make: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.model && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Model contains "${queryParams.model}"`}
                    onDelete={() => setQueryParams({ ...queryParams, model: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}
            </Grid>

            <Grid item xs={12}>
              <ListBody>
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <OrderableTableCell<SendBackToCanadaDto>
                              keyName='createdDateTime'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof SendBackToCanadaDto | undefined}
                            >
                              Created
                            </OrderableTableCell>

                            <OrderableTableCell<SendBackToCanadaDto>
                              keyName='vehicleCheckInDateTime'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof SendBackToCanadaDto | undefined}
                            >
                              Vehicle Check-In Completed Date
                            </OrderableTableCell>

                            <OrderableTableCell<SendBackToCanadaDto>
                              keyName='dotRejectionDateTime'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof SendBackToCanadaDto | undefined}
                            >
                              Rejection Date
                            </OrderableTableCell>

                            <OrderableTableCell<SendBackToCanadaDto>
                              keyName='vin'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof SendBackToCanadaDto | undefined}
                            >
                              VIN
                            </OrderableTableCell>

                            <OrderableTableCell<SendBackToCanadaDto>
                              keyName='year'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof SendBackToCanadaDto | undefined}
                            >
                              Year
                            </OrderableTableCell>

                            <OrderableTableCell<SendBackToCanadaDto>
                              keyName='make'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof SendBackToCanadaDto | undefined}
                            >
                              Make
                            </OrderableTableCell>

                            <OrderableTableCell<SendBackToCanadaDto>
                              keyName='model'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof SendBackToCanadaDto | undefined}
                            >
                              Model
                            </OrderableTableCell>

                            <TableCell padding='checkbox' size='small'></TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {toDoList.map((item, index) => (
                            <TableRow key={item.vehicleGuid} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                              <TableCell>{formatDateMoment(item.createdDateTime)}</TableCell>

                              <TableCell>{item.vehicleCheckInDateTime ? formatDateMoment(item.vehicleCheckInDateTime) : '-'}</TableCell>

                              <TableCell
                                sx={theme => ({
                                  color:
                                    item.dotRejectionDateTime && moment().diff(moment(item.dotRejectionDateTime), 'days') >= 1
                                      ? theme.palette.error.main
                                      : 'inherit',
                                  fontWeight: item.dotRejectionDateTime && moment().diff(moment(item.dotRejectionDateTime), 'days') >= 1 ? 'bold' : 'inherit'
                                })}
                              >
                                {item.dotRejectionDateTime ? formatDateMoment(item.dotRejectionDateTime) : '-'}
                              </TableCell>

                              <TableCell>{item.vin}</TableCell>

                              <TableCell>{item.year}</TableCell>

                              <TableCell>{item.make}</TableCell>

                              <TableCell>{item.model}</TableCell>

                              <TableCell padding='checkbox' size='small'>
                                <Button
                                  color='primary'
                                  endIcon={<Edit />}
                                  onClick={() => navigate(`/vehicles/${encodeURIComponent(item.vehicleGuid)}/conformity`)}
                                  size='small'
                                  variant='text'
                                >
                                  EDIT
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>

                        <TableFooter>
                          <TableRow>
                            <TablePagination
                              rowsPerPageOptions={[5, 10, 25]}
                              SelectProps={{ fullWidth: false }}
                              count={totalCount}
                              rowsPerPage={queryParams.pageSize}
                              page={queryParams.page}
                              onPageChange={(_, page) => {
                                setQueryParams({
                                  ...queryParams,
                                  page
                                })
                              }}
                              onRowsPerPageChange={e => {
                                setQueryParams({
                                  ...queryParams,
                                  page: 0,
                                  pageSize: e.target.value as unknown as number
                                })
                              }}
                            />
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </TableContainer>
                  </Table>
                </Box>
              </ListBody>
            </Grid>

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**Send Back To Canada To Do List**

Vehicles appear in this to do list if Needs to be Sent Back to Canada = Yes on the “DOT Rejection” card on Vehicle Details – Conformity (tab), but “Sent Back To Canada” and/or “Record #” fields are blank on the “Send Back To Canada” card on Vehicle Details – Conformity (tab).

The expected use case for this screen is that users will see any vehicles listed here, navigate to the “Vehicle Details – Conformity (tab)” for the vehicle, and then enter the required fields on the “Send Back To Canada” card. In effect, users should keep this “to do list” empty at all times.

This list is sorted by Created Date (oldest to newest).

**Select To Do List Button:**

Clicking this button will open the “Select To-Do List” dialog.

**Add VIN Button:**

Clicking this button will open the “Add VIN” dialog.

**Filter Field Drop-Down:**

Clicking the drop-down displays a list of columns available for filtering:

   - Created,
   - Vehicle Check-In Completed Date,
   - Rejection Date,
   - VIN,
   - Year,
   - Make,
   - Model

**Filter Value:**

The "Filter Value" field dynamically adjusts based on the selected column in the "Filter Field Drop-Down":

   - Created: Displays Filter Date From and Filter Date To.
   - Vehicle Check-In Completed Date: Displays Filter Date From and Filter Date To.
   - Rejection Date: Displays Filter Date From and Filter Date To.
   - VIN: Displays a free-type filter field.
   - Year: Displays a free-type filter field.
   - Make: Displays a free-type filter field.
   - Model: Displays a free-type filter field.

**Apply Filter Button:**

Clicking this button applies the selected filtering options and updates the displayed data.

**Reset Filters:**

Clicking this button clears all applied filtering options, returning the data view to its default state.

**Vehicle Check-In Date Completed Column:**

The Vehicle Check-In Date represents the date the first vehicle check-in image was associated with the vehicle.

**Rejection Date:**

Rejection Date is bold and red when current date is ≥ Rejection Date + 1 day.

**Edit Action:**

Clicking the "EDIT" text button navigates the user to Vehicle Details – Conformity (tab) for that vehicle.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
